import React, { useState } from "react";
import { isPlaceholderEvent } from "../shared/utils/eventPlaceholder";
import { copyEventRegistrationUrl } from "../shared/api/eventRegistrationUrl";
import { getEventShareLinkLabels } from "../shared/utils/eventShareLinkLabels";
import { IconLink, iconBtnStyle } from "../shared/icons/IconLib";

/**
 * Renders a QR code for an event share link (vanity `/e/{slug}` when set).
 * Public events: calendar registration. Private events: staff check-in via slug redirect.
 * QR is generated by the API so web and mobile share the same logic.
 */
// Display size (px). API image is requested larger for crisp scan-ability on high-DPI and at distance.
const QR_DISPLAY_SIZE = 120;
const QR_API_SIZE = 200; // higher-res image so QR is easier to scan without zooming

function EventRegistrationQRCode({ event, accountId, serverUrl, size = QR_DISPLAY_SIZE, className = "" }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");

  if (!event?._id || !accountId || !serverUrl) return null;
  if (isPlaceholderEvent(event)) return null;

  const { qrTitle, qrAlt, copyLabel, copiedLabel, copyErrorFallback } = getEventShareLinkLabels(event);

  const displaySize = Math.min(160, Math.max(96, size));
  const apiSize = Math.min(512, Math.max(QR_API_SIZE, displaySize * 2));

  const origin = typeof window !== "undefined" ? window.location?.origin : "";
  const params = new URLSearchParams({
    eventId: String(event._id),
    accountId: String(accountId),
    size: String(apiSize),
  });
  if (origin) params.set("baseUrl", origin);

  const qrSrc = `${serverUrl.replace(/\/$/, "")}/getEventRegistrationQR?${params.toString()}`;

  const handleCopyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCopyError("");
    try {
      await copyEventRegistrationUrl({
        eventId: event._id,
        accountId,
        origin,
      });
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyError(err && err.message ? err.message : copyErrorFallback);
    }
  };

  return (
    <div className={className} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div title={qrTitle}>
        <img src={qrSrc} alt={qrAlt} width={displaySize} height={displaySize} style={{ display: "block" }} />
      </div>
      <button
        type="button"
        className="btn-link"
        style={{ ...iconBtnStyle, gap: 4, fontSize: "0.72rem", minHeight: 22, whiteSpace: "nowrap" }}
        onClick={handleCopyLink}
        title={copyError || copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        <IconLink size={14} />
        <span>{copied ? "Copied!" : "Copy link"}</span>
      </button>
    </div>
  );
}

export default EventRegistrationQRCode;
